import React from 'react'
import PropTypes from 'prop-types'
import { renderRichText } from '../../../gutenberg/helpers'
import { useInnerBlocks } from '../../../gutenberg/utils'
import SVG from '../SVGs'
import { textColor } from '../../util/textColor'

// Styled Components
import { Wrapper, Container, Row, Col } from 'scripts/styled-components'
import {
  Content,
  Description,
  Headline,
  Title,
  Teaser,
  SecondHeadline,
  ButtonWrapper,
  ImagesDesktopWrapper,
  ImagesDesktop,
  ImagesMobile,
} from './custom.styled'

const OverlappingImages = ({
  reverse_columns = false,
  remove_left_space = false,
  anchor_slug,
  headline,
  title,
  teaser,
  children,
  second_headline,
  add_decors,
  bg_color,
  large_image,
  small_image,
}) => {
  const { InnerBlocks, ref } = useInnerBlocks(children, Col)

  return (
    <Wrapper
      id={anchor_slug || null}
      className={'dhsv-overlapping-images'}
      bgColor={bg_color}
      textColor={textColor(bg_color)}
    >
      <Container padding={'90px 0 80px'} mobilePadding={'60px 0 30px'}>
        <Row direction={reverse_columns ? 'row-reverse' : 'row'}>
          <Col size={5} className={'hide-md-down'}>
            <ImagesDesktopWrapper
              className={reverse_columns ? 'reversed-column' : null}
            >
              <ImagesDesktop
                className={
                  'overlapping-images__wide overlapping-images__row-image'
                }
              >
                {large_image.url !== null && (
                  <img
                    src={large_image.url}
                    className={'overlapping-images__large-image'}
                    alt={large_image.alt || large_image.title}
                    width={large_image.width}
                    height={large_image.height}
                  />
                )}
                {small_image.url !== null && (
                  <img
                    src={small_image.url}
                    className={'overlapping-images__small-image'}
                    alt={small_image.alt || small_image.title}
                    width={small_image.width}
                    height={small_image.height}
                  />
                )}
              </ImagesDesktop>
            </ImagesDesktopWrapper>
          </Col>

          <Col size={7}>
            <Content className={reverse_columns ? 'reversed-column' : null}>
              {headline && (
                <Headline>{renderRichText(headline, 'span')}</Headline>
              )}

              {title && (
                <Title>
                  {renderRichText(title, 'span')}
                  {add_decors === 'decor-line' && (
                    <SVG
                      icon={'Line'}
                      className={'decor-line'}
                      type={'theme'}
                    />
                  )}

                  {add_decors === 'decor-wave' && (
                    <SVG
                      icon={'Wave'}
                      className={'decor-wave'}
                      type={'theme'}
                    />
                  )}

                  {add_decors === 'decor-circle' && (
                    <SVG
                      icon={'Circle'}
                      className={'decor-circle'}
                      type={'theme'}
                    />
                  )}
                </Title>
              )}

              <ImagesMobile>
                {large_image.url !== null && (
                  <img
                    src={large_image.url}
                    className={'overlapping-images__large-image'}
                    alt={large_image.alt || large_image.title}
                    width={large_image.width}
                    height={large_image.height}
                  />
                )}
                {small_image.url !== null && (
                  <img
                    src={small_image.url}
                    className={'overlapping-images__small-image'}
                    alt={small_image.alt || small_image.title}
                    width={small_image.width}
                    height={small_image.height}
                  />
                )}
              </ImagesMobile>

              <Description
                removeSpace={remove_left_space}
                reversedColumns={reverse_columns}
              >
                {teaser && <Teaser>{renderRichText(teaser, 'span')}</Teaser>}

                {second_headline && (
                  <SecondHeadline>
                    {renderRichText(second_headline, 'span')}
                    {add_decors && (
                      <SVG
                        icon={'Line'}
                        className={'overlapping-images__decor-line'}
                        type={'theme'}
                      />
                    )}
                  </SecondHeadline>
                )}

                <ButtonWrapper ref={ref}>{InnerBlocks}</ButtonWrapper>
              </Description>
            </Content>
          </Col>
        </Row>
      </Container>
    </Wrapper>
  )
}

const richTextType = PropTypes.oneOfType([PropTypes.string, PropTypes.node])

OverlappingImages.propTypes = {
  reverse_columns: PropTypes.bool,
  remove_left_space: PropTypes.bool,
  anchor_slug: PropTypes.string,
  headline: richTextType,
  title: richTextType,
  teaser: richTextType,
  second_headline: richTextType,
  add_decors: PropTypes.string,
  bg_color: PropTypes.string,
  large_image: PropTypes.object,
  small_image: PropTypes.object,
  children: PropTypes.string,
}

export default OverlappingImages
